import api from "services/api";
import auth from "services/auth";
import supportMocks from "support/api/supportMocks";
import {
  getVisitorToken,
  setVisitorToken,
  clearVisitorToken,
} from "support/storage/supportStorage";

// The one door to the Concierge API. It sits on top of services/api so the
// player Bearer, language and country headers keep working, and adds the two
// things that client does not know about: the parallel X-Support-Visitor-Token
// header, and the fact that a 401 on a visitor call means re-mint, not refresh.
// Every method resolves to the {status, data} shape services/api returns and
// never throws, so callers branch on status like the rest of the app.

const BASE = "/support";
const MOCK_KEY = "sup.mock.v1";

// The mock backend is build scaffolding, never a production capability. It is
// hard-disabled on the production build, so ?supportMock=1 on the live site is
// inert: without this it would be a URL anyone could use to swap the real
// support panel for an unreleased UI, and because the choice is remembered per
// tab it would keep doing so on every later page load in that tab.
const MOCK_ALLOWED = process.env.REACT_APP_NODE_ENV !== "production";

function readMockFlag() {
  if (!MOCK_ALLOWED) {
    return false;
  }
  try {
    const query = new URLSearchParams(window.location.search);
    if (query.has("supportMock")) {
      const on = query.get("supportMock") !== "0";
      window.sessionStorage.setItem(MOCK_KEY, on ? "1" : "0");
      return on;
    }
    return window.sessionStorage.getItem(MOCK_KEY) === "1";
  } catch (error) {
    return false;
  }
}

let mockEnabled = readMockFlag();

export function isMockEnabled() {
  return mockEnabled;
}

export function setMockEnabled(on) {
  mockEnabled = MOCK_ALLOWED && !!on;
  try {
    window.sessionStorage.setItem(MOCK_KEY, mockEnabled ? "1" : "0");
  } catch (error) {}
  return mockEnabled;
}

function visitorHeaders() {
  const token = getVisitorToken();
  return token ? { "X-Support-Visitor-Token": token } : {};
}

let mintInFlight = null;

async function mintVisitorToken() {
  if (mintInFlight) {
    return mintInFlight;
  }
  mintInFlight = (async () => {
    const existing = getVisitorToken();
    const response = mockEnabled
      ? await supportMocks.mintVisitorToken(existing)
      : await api.post(
          BASE + "/visitor-token",
          {},
          existing ? { "X-Support-Visitor-Token": existing } : {}
        );
    if (response.status === 200 && response.data && response.data.visitor_token) {
      setVisitorToken(response.data.visitor_token, response.data.expires_in);
    }
    return response;
  })();
  try {
    return await mintInFlight;
  } finally {
    mintInFlight = null;
  }
}

// Anything behind the supportprincipal middleware needs either a player Bearer
// or a visitor token; without one the request is a guaranteed 401.
async function ensurePrincipal() {
  if (auth.isAuthenticated() || getVisitorToken()) {
    return true;
  }
  const response = await mintVisitorToken();
  return response.status === 200;
}

// A visitor 401 means the token expired or the signing key rotated. Re-mint once
// and replay. services/api only retries 401s for authenticated players, so this
// path is the visitor equivalent and must not be folded into it.
async function withPrincipal(send) {
  await ensurePrincipal();
  let response = await send();
  if (response && response.status === 401 && !auth.isAuthenticated()) {
    clearVisitorToken();
    const minted = await mintVisitorToken();
    if (minted.status === 200) {
      response = await send();
    }
  }
  return response;
}

const supportApi = {
  mintVisitorToken,

  // The serving flag lives on the shared /initial-settings call, not under
  // /support, so mock mode has to answer for it as well or the cutover path
  // cannot be exercised until the backend flag ships. On production mockEnabled
  // is always false, so this is the backend flag and nothing else.
  isMessengerServed(initialSettings) {
    if (mockEnabled) {
      return true;
    }
    return !!(initialSettings && initialSettings.support_messenger_enabled);
  },

  getWidgetConfig(locale) {
    if (mockEnabled) {
      return supportMocks.widgetConfig(locale);
    }
    return api.get(BASE + "/widget-config", locale ? { locale } : undefined);
  },

  createConversation(payload) {
    if (mockEnabled) {
      return supportMocks.createConversation(payload);
    }
    return withPrincipal(() => api.post(BASE + "/conversations", payload, visitorHeaders()));
  },

  listConversations(params) {
    if (mockEnabled) {
      return supportMocks.listConversations(params);
    }
    return withPrincipal(() => api.get(BASE + "/conversations", params, visitorHeaders()));
  },

  getConversation(reference) {
    if (mockEnabled) {
      return supportMocks.getConversation(reference);
    }
    return withPrincipal(() =>
      api.get(BASE + "/conversations/" + encodeURIComponent(reference), undefined, visitorHeaders())
    );
  },

  postMessage(reference, payload) {
    if (mockEnabled) {
      return supportMocks.postMessage(reference, payload);
    }
    return withPrincipal(() =>
      api.post(
        BASE + "/conversations/" + encodeURIComponent(reference) + "/messages",
        payload,
        visitorHeaders()
      )
    );
  },

  sendTyping(reference, isTyping) {
    if (mockEnabled) {
      return supportMocks.typing(reference, isTyping);
    }
    return withPrincipal(() =>
      api.post(
        BASE + "/conversations/" + encodeURIComponent(reference) + "/typing",
        { is_typing: !!isTyping },
        visitorHeaders()
      )
    );
  },

  markRead(reference, lastMessageId) {
    if (mockEnabled) {
      return supportMocks.markRead(reference, lastMessageId);
    }
    return withPrincipal(() =>
      api.post(
        BASE + "/conversations/" + encodeURIComponent(reference) + "/read",
        { last_message_id: lastMessageId },
        visitorHeaders()
      )
    );
  },

  submitCsat(reference, payload) {
    if (mockEnabled) {
      return supportMocks.submitCsat(reference, payload);
    }
    return withPrincipal(() =>
      api.post(
        BASE + "/conversations/" + encodeURIComponent(reference) + "/csat",
        payload,
        visitorHeaders()
      )
    );
  },

  sync(uuid, params) {
    if (mockEnabled) {
      return supportMocks.sync(uuid, params);
    }
    return withPrincipal(() =>
      api.get(
        BASE + "/conversations/" + encodeURIComponent(uuid) + "/sync",
        params,
        visitorHeaders()
      )
    );
  },
};

export default supportApi;
